import { Pill } from "@signet/ui";
import { ClaudeLogo } from "./logos.tsx";
import { Meter, ProviderCard, resetsLabel, useLiveUsage, useNow, type LiveResult } from "./parts.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Claude Max: how much of the subscription's allowance is used, the figures
   Claude Code shows under /usage. It is the plan the team codes with, not the
   API Signet's own features pay for per token (that is the Claude API
   section further down), and it has no dollar figure: the plan is a flat fee
   and the limit is the allowance in each window.

   The server reads it with CLAUDE_OAUTH_TOKEN and caches it for five
   minutes, because every read spends a sliver of the same allowance.
   ────────────────────────────────────────────────────────────────────────── */

type ClaudeWindow = { id: string; label: string; percent: number; resetsAt: string | null; status: string | null };
type ClaudeUsage = LiveResult & {
  source?: "usage-endpoint" | "rate-limit-headers";
  status?: string | null;
  windows?: ClaudeWindow[];
};

// The two windows everyone has come first, in this order; model-specific
// weekly windows (7-day Opus and so on) follow in the order the server sends.
const FIRST = ["five_hour", "seven_day"];

function ordered(windows: ClaudeWindow[]): ClaudeWindow[] {
  const rank = (w: ClaudeWindow) => (FIRST.includes(w.id) ? FIRST.indexOf(w.id) : FIRST.length);
  return windows.map((w, i) => ({ w, i })).sort((a, b) => rank(a.w) - rank(b.w) || a.i - b.i).map(({ w }) => w);
}

/** The rate limiter's word for where the account stands, when it is anything but fine. */
function statusPill(status: string | null | undefined) {
  if (!status || status === "allowed") return null;
  if (status === "allowed_warning") return <Pill tone="warn" dot>Near the limit</Pill>;
  if (status === "rejected") return <Pill tone="bad" dot>Limit reached</Pill>;
  return <Pill tone="idle">{status.replace(/_/g, " ")}</Pill>;
}

export function ClaudeMaxCard() {
  const live = useLiveUsage<ClaudeUsage>("claude", 5 * 60_000);
  const now = useNow();
  return (
    <ProviderCard
      id="claude"
      title="Claude Max"
      logo={ClaudeLogo}
      caption="The subscription allowance Claude Code shows in /usage, not API billing."
      envVar="CLAUDE_OAUTH_TOKEN"
      live={live}
    >
      {(data) => {
        const windows = ordered(data.windows ?? []);
        return (
          <div className="signet-usage-stack">
            {statusPill(data.status)}
            {windows.length ? (
              windows.map((w) => <Meter key={w.id} label={w.label} percent={w.percent} detail={resetsLabel(w.resetsAt, now)} />)
            ) : (
              <p className="faint">No usage windows reported yet.</p>
            )}
          </div>
        );
      }}
    </ProviderCard>
  );
}
